import { Field } from '@atlaskit/form';
import FiltersDropdown from './dropdown/filters-dropdown';
import TextField from '@atlaskit/textfield';
import { SelectOption } from 'core/utilities/interface-helpers';
import Select from '@atlaskit/select';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SharedButton from '../buttons/button';
import { DatePicker } from '@atlaskit/datetime-picker';

export type Filter = {
  id: string;
  document?: string;
  label: string;
  options?: SelectOption<string>[];
  date?: boolean;
};

export type SelectedValue = {
  id: string;
  value: string | string[];
};

type SharedFiltersProps = {
  filters: Filter[];
  onFilterValueChange: (values: SelectedValue[]) => void;
  limit?: number;
};

const SharedFilters = ({ filters, onFilterValueChange, limit }: SharedFiltersProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedValues, setSelectedValues] = useState<SelectedValue[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([]);

  useEffect(() => {
    const currentFilters = Array.from(searchParams.entries());

    const filteredFilters = currentFilters
      .map(([key, value]) => filters.find((f) => f.id === key) as Filter)
      .filter((f) => f !== undefined) as Filter[];

    setSelectedFilters(filteredFilters);
    setSelectedValues([
      ...filteredFilters.map((filter) => ({
        id: filter.id,
        value: searchParams.get(filter.id) ?? '',
      })),
    ]);
  }, [searchParams, filters]);

  useEffect(() => {
    onFilterValueChange(selectedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues]);

  const updateFilter = (filterId: string, value?: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    const urlFilters = Array.from(newSearchParams.entries()).map(([key]) => key);

    if (urlFilters.includes(filterId) && value === undefined) {
      newSearchParams.delete(filterId);
    } else {
      newSearchParams.set(filterId, value ?? '');
    }

    setSearchParams(newSearchParams, { replace: true });
  };

  return (
    <>
      <div className='flex mb-2 items-end justify-between'>
        <div className='flex gap-x-2 flex-wrap'>
          {filters
            .filter((filter) => selectedFilters.some((f) => f.id === filter.id))
            .map((filter) => (
              <div key={filter.id} className='min-w-[200px] relative group'>
                <Field name={filter.id} label={filter.label}>
                  {({ fieldProps }) => {
                    if (filter.options) {
                      const options = [{ value: '', label: 'Select...', isDisabled: true }, ...filter.options];
                      return (
                        <Select
                          {...fieldProps}
                          placeholder={filter.label}
                          isSearchable={true}
                          value={options?.find(
                            (option) => option.value === selectedValues.find((v) => v.id === filter.id)?.value
                          )}
                          options={options}
                          onChange={(value) => updateFilter(filter.id, value?.value)}
                        />
                      );
                    } else if (filter.date) {
                      return (
                        <DatePicker
                          {...fieldProps}
                          placeholder={filter.label}
                          locale={'en-UK'}
                          value={selectedValues.find((v) => v.id === filter.id)?.value as string}
                          onChange={(value) => updateFilter(filter.id, value)}
                        />
                      );
                    }
                    return (
                      <TextField
                        {...fieldProps}
                        placeholder={filter.label}
                        type='text'
                        value={selectedValues.find((v) => v.id === filter.id)?.value}
                        onChange={(value) => updateFilter(filter.id, (value.target as any).value)}
                      />
                    );
                  }}
                </Field>
              </div>
            ))}
        </div>
        <div className='flex gap-x-2 mt-2'>
          <FiltersDropdown
            limit={limit}
            filters={filters}
            selectedFilters={selectedFilters}
            onFilterToggle={(filter) => updateFilter(filter.id)}
            onRemoveAll={() => {
              const newSearchParams = new URLSearchParams(searchParams);
              selectedFilters.forEach((filter) => {
                newSearchParams.delete(filter.id);
              });
              setSearchParams(newSearchParams, { replace: true });
            }}
          />
          <SharedButton
            onClick={() => {
              const newSearchParams = new URLSearchParams(searchParams);
              selectedFilters.forEach((filter) => {
                newSearchParams.set(filter.id, '');
              });
              setSearchParams(newSearchParams, { replace: true });
            }}
            type='button'
            appearance='primary'
            label='Clear'
          />
        </div>
      </div>
    </>
  );
};

export default SharedFilters;
